export type LegalBlock =
  | { type: "p"; text: string }
  | { type: "note"; text: string }
  | { type: "subheading"; text: string }
  | { type: "list"; items: string[] }
  | { type: "contact"; lines: string[]; email: string; website: string };

export interface LegalSection {
  id: string;
  title: string;
  blocks: LegalBlock[];
}

export interface LegalDocument {
  title: string;
  effectiveDate: string;
  intro: string;
  sections: LegalSection[];
}

export const privacyPolicy: LegalDocument = {
  title: "Privacy Policy",
  effectiveDate: "July 9, 2026",
  intro:
    'Matrix Rental Solutions ("Matrix," "we," "our," or "us") respects your privacy and is committed to protecting the personal and financial information you entrust to us. This Privacy Policy explains how we collect, use, disclose, and protect information when you use the Matrix Rental Solutions platform, including our Universal Rental Application ("URA"), MAIA (Matrix Asset & Income Analysis), and related services.',
  sections: [
    {
      id: "information-we-collect",
      title: "Information We Collect",
      blocks: [
        { type: "p", text: "Depending on how you use our services, we may collect:" },
        { type: "subheading", text: "Personal Information" },
        {
          type: "list",
          items: [
            "Name",
            "Address",
            "Email address",
            "Phone number",
            "Date of birth",
            "Government-issued identification",
            "Employment information",
            "Rental history",
            "Household information",
          ],
        },
        { type: "subheading", text: "Financial Information" },
        {
          type: "p",
          text:
            "With your explicit authorization, we may collect financial information from connected financial institutions through third-party financial data providers. This may include:",
        },
        {
          type: "list",
          items: [
            "Bank account ownership information",
            "Account balances",
            "Transaction history",
            "Income deposits",
            "Account type",
            "Routing and account information (tokenized when available)",
          ],
        },
        { type: "note", text: "Matrix never collects your online banking credentials." },
      ],
    },
    {
      id: "how-we-use-your-information",
      title: "How We Use Your Information",
      blocks: [
        { type: "p", text: "We use your information to:" },
        {
          type: "list",
          items: [
            "Process rental applications",
            "Verify applicant identity",
            "Verify bank account ownership",
            "Analyze income and cash flow",
            "Calculate affordability and ability-to-pay metrics",
            "Detect fraud and document inconsistencies",
            "Support rental underwriting decisions",
            "Facilitate authorized ACH payments",
            "Improve our products and services",
            "Comply with legal obligations",
          ],
        },
        { type: "note", text: "Financial information is only accessed with your authorization." },
      ],
    },
    {
      id: "financial-account-connections",
      title: "Financial Account Connections",
      blocks: [
        {
          type: "p",
          text:
            "If you choose to connect a financial account, you authorize Matrix and our financial data providers to retrieve information from your financial institution on your behalf.",
        },
        { type: "p", text: "We use this information solely for purposes authorized by you, including:" },
        {
          type: "list",
          items: [
            "Income verification",
            "Asset verification",
            "Rental affordability analysis",
            "Account ownership verification",
            "Fraud prevention",
            "Payment authorization",
          ],
        },
        { type: "p", text: "You may disconnect your financial accounts at any time." },
      ],
    },
    {
      id: "sharing-information",
      title: "Sharing Information",
      blocks: [
        { type: "note", text: "We do not sell your personal information." },
        { type: "p", text: "We may share information with:" },
        {
          type: "list",
          items: [
            "Housing providers",
            "Property managers",
            "Government housing agencies",
            "Authorized screening providers",
            "Payment processors",
            "Identity verification providers",
            "Financial connectivity providers",
            "Service providers assisting in operating our platform",
            "Government agencies when required by law",
          ],
        },
        { type: "p", text: "We only share information necessary to perform requested services." },
      ],
    },
    {
      id: "third-party-service-providers",
      title: "Third-Party Service Providers",
      blocks: [
        { type: "p", text: "Matrix uses trusted third-party providers to perform services including:" },
        {
          type: "list",
          items: [
            "Payment processing",
            "Financial account connectivity",
            "Identity verification",
            "Credit reporting",
            "Fraud detection",
            "Cloud hosting",
            "Security monitoring",
          ],
        },
        {
          type: "p",
          text: "These providers may process personal information only for authorized business purposes.",
        },
      ],
    },
    {
      id: "data-security",
      title: "Data Security",
      blocks: [
        {
          type: "p",
          text:
            "Matrix maintains administrative, technical, and physical safeguards designed to protect your information. These measures include:",
        },
        {
          type: "list",
          items: [
            "Encryption in transit using TLS",
            "Encryption of sensitive data at rest",
            "Role-based access controls",
            "Audit logging",
            "Continuous security monitoring",
            "Secure cloud infrastructure",
          ],
        },
        {
          type: "p",
          text:
            "No system can guarantee absolute security; however, Matrix continually works to protect customer information.",
        },
      ],
    },
    {
      id: "data-retention",
      title: "Data Retention",
      blocks: [
        { type: "p", text: "We retain information only as long as necessary to:" },
        {
          type: "list",
          items: [
            "Provide requested services",
            "Meet legal obligations",
            "Resolve disputes",
            "Enforce agreements",
            "Maintain required business records",
          ],
        },
        {
          type: "p",
          text:
            "Financial data is retained only as necessary to support authorized services and applicable legal requirements.",
        },
      ],
    },
    {
      id: "your-rights",
      title: "Your Rights",
      blocks: [
        { type: "p", text: "Subject to applicable law, you may:" },
        {
          type: "list",
          items: [
            "Request access to your information",
            "Request correction of inaccurate information",
            "Request deletion of eligible information",
            "Withdraw consent for financial account access",
            "Request information regarding how your data is processed",
          ],
        },
        { type: "p", text: "Certain information may be retained where required by law." },
      ],
    },
    {
      id: "childrens-privacy",
      title: "Children's Privacy",
      blocks: [
        { type: "p", text: "Matrix services are not directed to children under 18 years of age." },
        { type: "p", text: "We do not knowingly collect information from children." },
      ],
    },
    {
      id: "changes-to-this-privacy-policy",
      title: "Changes to This Privacy Policy",
      blocks: [
        { type: "p", text: "We may update this Privacy Policy periodically." },
        {
          type: "p",
          text: "Material changes will be posted on this page with an updated Effective Date.",
        },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        {
          type: "contact",
          lines: ["Matrix Rental Solutions"],
          email: "info@mymatrixrent.com",
          website: "https://www.mymatrixrent.com",
        },
      ],
    },
    {
      id: "financial-data-disclosure",
      title: "Financial Data Disclosure",
      blocks: [
        { type: "p", text: "When you choose to connect a financial account through Matrix:" },
        {
          type: "list",
          items: [
            "You authorize Matrix and its authorized financial connectivity providers to access the account information you select.",
            "Matrix uses this information only to provide rental application, underwriting, affordability analysis, identity verification, fraud prevention, and payment-related services.",
            "Matrix does not access financial information without your authorization.",
            "You may revoke access at any time by disconnecting your linked account or contacting Matrix.",
          ],
        },
      ],
    },
  ],
};

export const termsOfService: LegalDocument = {
  title: "Terms of Service",
  effectiveDate: "July 9, 2026",
  intro:
    'Welcome to Matrix Rental Solutions ("Matrix," "we," "our," or "us"). These Terms of Service ("Terms") govern your access to and use of the Matrix Rental Solutions platform, including the Universal Rental Application (URA), MAIA (Matrix Asset & Income Analysis), our website, and related services. By accessing or using the Matrix platform, you agree to these Terms.',
  sections: [
    {
      id: "our-services",
      title: "Our Services",
      blocks: [
        {
          type: "p",
          text:
            "Matrix provides software that allows prospective residents, housing providers, property managers, affordable housing agencies, and other authorized organizations to:",
        },
        {
          type: "list",
          items: [
            "Submit rental applications",
            "Verify identity and financial information",
            "Connect financial accounts",
            "Analyze income, assets, and cash flow",
            "Complete rental screening workflows",
            "Process application fees and authorized payments",
            "Share application information with authorized housing providers",
          ],
        },
        {
          type: "p",
          text:
            "Matrix is a technology platform. Matrix does not own, lease, or manage rental properties and is not a party to any lease agreement.",
        },
      ],
    },
    {
      id: "eligibility",
      title: "Eligibility",
      blocks: [
        { type: "p", text: "You must:" },
        {
          type: "list",
          items: [
            "Be at least 18 years old or the age of majority in your jurisdiction.",
            "Have the legal authority to enter into these Terms.",
            "Provide accurate and complete information.",
          ],
        },
      ],
    },
    {
      id: "user-responsibilities",
      title: "User Responsibilities",
      blocks: [
        { type: "p", text: "You agree to:" },
        {
          type: "list",
          items: [
            "Provide truthful information.",
            "Submit only documents that belong to you or that you are authorized to provide.",
            "Maintain the security of your account.",
            "Notify Matrix of unauthorized account activity.",
            "Use the platform only for lawful purposes.",
          ],
        },
        { type: "p", text: "You may not:" },
        {
          type: "list",
          items: [
            "Attempt to interfere with the operation of the platform.",
            "Upload fraudulent documents.",
            "Misrepresent your identity.",
            "Attempt unauthorized access to Matrix systems.",
          ],
        },
      ],
    },
    {
      id: "financial-account-connections",
      title: "Financial Account Connections",
      blocks: [
        {
          type: "p",
          text:
            "Certain services allow you to connect financial accounts through third-party financial data providers. By connecting an account, you authorize Matrix and its authorized providers to:",
        },
        {
          type: "list",
          items: [
            "Verify account ownership.",
            "Retrieve balances.",
            "Retrieve transaction history.",
            "Analyze income and cash flow.",
            "Support rental underwriting.",
            "Facilitate authorized payment transactions.",
          ],
        },
        {
          type: "p",
          text:
            "You may revoke access by disconnecting your financial account, subject to any ongoing processing required to complete an application or authorized transaction.",
        },
        { type: "note", text: "Matrix never receives or stores your online banking credentials." },
      ],
    },
    {
      id: "rental-screening",
      title: "Rental Screening",
      blocks: [
        {
          type: "p",
          text:
            "Matrix may analyze information you provide to generate affordability metrics, income summaries, fraud indicators, and other analytical outputs. These analyses are intended to assist housing providers and authorized agencies.",
        },
        { type: "p", text: "Matrix does not guarantee approval for housing." },
        {
          type: "p",
          text:
            "Final rental decisions are made solely by the housing provider or other authorized decision-maker.",
        },
      ],
    },
    {
      id: "payments",
      title: "Payments",
      blocks: [
        {
          type: "p",
          text:
            "Certain services require payment of application fees or other authorized charges. By submitting payment, you authorize Matrix and its payment processors to:",
        },
        {
          type: "list",
          items: [
            "Charge your selected payment method.",
            "Process ACH payments where authorized.",
            "Collect applicable taxes and fees.",
          ],
        },
        { type: "p", text: "Unless otherwise stated, application fees are non-refundable." },
      ],
    },
    {
      id: "government-housing-programs",
      title: "Government Housing Programs",
      blocks: [
        { type: "p", text: "Matrix may be used by:" },
        {
          type: "list",
          items: [
            "Housing Authorities",
            "State agencies",
            "Local governments",
            "Nonprofit organizations",
            "Affordable housing providers",
          ],
        },
        {
          type: "p",
          text:
            "Additional program-specific rules may apply when participating in government-sponsored housing programs.",
        },
      ],
    },
    {
      id: "third-party-services",
      title: "Third-Party Services",
      blocks: [
        { type: "p", text: "Matrix integrates with third-party providers, including:" },
        {
          type: "list",
          items: [
            "Payment processors",
            "Financial connectivity providers",
            "Credit reporting agencies",
            "Identity verification providers",
            "Fraud detection providers",
            "Cloud hosting providers",
          ],
        },
        {
          type: "p",
          text:
            "Your use of certain services may also be subject to those providers' terms and privacy policies.",
        },
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      blocks: [
        {
          type: "p",
          text:
            "The Matrix platform, MAIA, software, algorithms, documentation, trademarks, logos, and related content are owned by Matrix Rental Solutions or its licensors.",
        },
        { type: "p", text: "You may not:" },
        {
          type: "list",
          items: ["Copy", "Reverse engineer", "Modify", "Redistribute", "Sell", "License"],
        },
        { type: "p", text: "any portion of the platform without written permission." },
      ],
    },
    {
      id: "availability",
      title: "Availability",
      blocks: [
        {
          type: "p",
          text:
            "We strive to provide reliable services but do not guarantee uninterrupted availability.",
        },
        {
          type: "p",
          text: "Maintenance, third-party outages, or technical issues may temporarily affect service.",
        },
      ],
    },
    {
      id: "disclaimer",
      title: "Disclaimer",
      blocks: [
        {
          type: "p",
          text:
            "Matrix provides technology to facilitate rental applications and financial analysis. Except as expressly stated, services are provided \"AS IS\" and \"AS AVAILABLE.\"",
        },
        {
          type: "p",
          text: "Matrix disclaims all warranties to the maximum extent permitted by law.",
        },
      ],
    },
    {
      id: "limitation-of-liability",
      title: "Limitation of Liability",
      blocks: [
        {
          type: "p",
          text: "To the fullest extent permitted by law, Matrix shall not be liable for:",
        },
        {
          type: "list",
          items: [
            "Indirect damages",
            "Lost profits",
            "Lost opportunities",
            "Data loss",
            "Business interruption",
          ],
        },
        {
          type: "p",
          text: "Matrix's total liability arising from these Terms shall not exceed the greater of:",
        },
        {
          type: "list",
          items: ["$100, or", "the amount you paid Matrix during the preceding twelve months."],
        },
        { type: "note", text: "Nothing in these Terms limits liability that cannot legally be limited." },
      ],
    },
    {
      id: "indemnification",
      title: "Indemnification",
      blocks: [
        {
          type: "p",
          text:
            "You agree to indemnify and hold harmless Matrix, its officers, employees, contractors, and affiliates from claims arising out of:",
        },
        {
          type: "list",
          items: [
            "Your misuse of the platform.",
            "Your violation of these Terms.",
            "Information you submit.",
            "Your violation of applicable law.",
          ],
        },
      ],
    },
    {
      id: "suspension-and-termination",
      title: "Suspension and Termination",
      blocks: [
        { type: "p", text: "Matrix may suspend or terminate access if:" },
        {
          type: "list",
          items: [
            "You violate these Terms.",
            "Fraud is suspected.",
            "Required by law.",
            "Necessary to protect the security of the platform.",
          ],
        },
      ],
    },
    {
      id: "privacy",
      title: "Privacy",
      blocks: [{ type: "p", text: "Your use of Matrix is also governed by our Privacy Policy." }],
    },
    {
      id: "electronic-communications",
      title: "Electronic Communications",
      blocks: [
        {
          type: "p",
          text:
            "By using Matrix, you consent to receive electronic communications relating to your account, rental applications, payment confirmations, and platform notices.",
        },
      ],
    },
    {
      id: "changes-to-these-terms",
      title: "Changes to These Terms",
      blocks: [
        { type: "p", text: "Matrix may modify these Terms from time to time." },
        {
          type: "p",
          text:
            "Material changes will become effective upon posting an updated version with a revised Effective Date.",
        },
        {
          type: "p",
          text: "Continued use of the platform constitutes acceptance of the revised Terms.",
        },
      ],
    },
    {
      id: "governing-law",
      title: "Governing Law",
      blocks: [
        {
          type: "p",
          text:
            "These Terms are governed by the laws of the State of Connecticut, without regard to its conflict of laws principles.",
        },
        {
          type: "p",
          text:
            "Any dispute arising under these Terms shall be brought in the state or federal courts located in Connecticut, unless applicable law requires otherwise.",
        },
      ],
    },
    {
      id: "contact-information",
      title: "Contact Information",
      blocks: [
        {
          type: "contact",
          lines: ["Matrix Rental Solutions"],
          email: "info@mymatrixrent.com",
          website: "https://www.mymatrixrent.com",
        },
      ],
    },
  ],
};
